import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '../../contexts/StoreProvider';
import { RefreshCw, Package } from 'lucide-react';

const OrderHistoryScreen = () => {
  const { orders, fetchOrders, isOrdersLoading } = useStore();
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-orange-100 text-orange-800';
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex justify-between items-center mb-8 border-b pb-4">
        <h1 className="text-2xl font-bold tracking-widest uppercase">Order History</h1>
        <button 
          onClick={fetchOrders}
          disabled={isOrdersLoading}
          className="flex items-center text-sm font-bold uppercase tracking-wider text-gray-600 hover:text-black transition-colors disabled:opacity-50"
        >
          <RefreshCw size={16} className={`mr-2 ${isOrdersLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {isOrdersLoading && orders.length === 0 ? (
        <div className="flex justify-center py-20">
          <div className="w-10 h-10 border-4 border-black border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-32 text-center">
          <Package size={48} className="text-gray-300 mb-4" />
          <h2 className="text-xl font-bold text-black mb-2">No Orders Found</h2>
          <p className="text-gray-500">You haven't placed any orders yet.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map((order) => (
            <div key={order.id} className="bg-white border border-gray-200 p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center hover:shadow-md transition-shadow">
              <div className="flex flex-col mb-4 sm:mb-0">
                <div className="flex items-start gap-4 mb-3">
                  {order.items && order.items.length > 0 && (
                    <div className="w-16 h-16 bg-gray-100 flex-shrink-0 flex items-center justify-center">
                      {(order.items[0].product_detail || order.items[0].product)?.images?.[0]?.image ? (
                        <img 
                          src={(order.items[0].product_detail || order.items[0].product).images[0].image} 
                          alt="Product" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Package size={24} className="text-gray-400" />
                      )}
                    </div>
                  )}
                  <div>
                    <span className="text-sm text-gray-500 font-semibold mb-1 block">
                      {new Date(order.created_at).toLocaleDateString('en-US', {
                        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </span>
                    <Link to={`/store/orders/${order.id}`} className="text-lg font-bold hover:underline block">
                      Order #{order.order_number}
                    </Link>
                    {order.items && order.items.length > 0 && (
                      <span className="text-sm text-gray-800 font-medium block mt-1">
                        {(order.items[0].product_detail || order.items[0].product)?.name}
                        {order.items.length > 1 && ` + ${order.items.length - 1} more items`}
                      </span>
                    )}
                    <span className="text-sm text-gray-600 mt-1 block">Total: ₵{parseFloat(order.total_amount).toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:items-end gap-2">
                <div className="flex gap-2">
                  <span className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full ${getStatusColor(order.status)}`}>
                    {order.status}
                  </span>
                  <span className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full ${getPaymentStatusColor(order.payment_status)}`}>
                    {order.payment_status}
                  </span>
                </div>
                <button
                  onClick={() => navigate(`/store/orders/${order.id}`)}
                  className="mt-2 text-sm font-bold uppercase tracking-wider underline hover:text-gray-600"
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrderHistoryScreen;
